import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface StaffLeave {
	id: number;
	staff_id: number;
	leave_date: string;
	leave_type: 'full_day' | 'half_day_morning' | 'half_day_afternoon';
	reason: string | null;
	approved_by: number | null;
	staff?: {
		id: number;
		first_name: string;
		last_name: string;
		full_name: string;
		role: string;
	};
	approver?: {
		id: number;
		name: string;
	};
	created_at: string;
	updated_at: string;
}

export interface ApiResponse<T> {
	status: boolean;
	data: T;
	message?: string;
}

export interface PaginatedResponse<T> {
	current_page: number;
	data: T[];
	last_page: number;
	per_page: number;
	total: number;
}

export interface LeaveRequest {
	staff_id: number;
	leave_date: string;
	leave_type: 'full_day' | 'half_day_morning' | 'half_day_afternoon';
	reason?: string;
}

export interface BulkLeaveRequest {
	staff_id: number;
	dates: string[];
	leave_type: 'full_day' | 'half_day_morning' | 'half_day_afternoon';
	reason?: string;
}

@Injectable({
	providedIn: 'root',
})
export class LeaveService {
	private http = inject(HttpClient);
	private apiUrl = environment.apiUrl;

	getAll(filters?: { staff_id?: number; start_date?: string; end_date?: string; date?: string }): Observable<ApiResponse<PaginatedResponse<StaffLeave>>> {
		let params = new HttpParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					params = params.set(key, value.toString());
				}
			});
		}
		return this.http.get<ApiResponse<PaginatedResponse<StaffLeave>>>(`${this.apiUrl}/admin/leaves`, { params });
	}

	getById(id: number): Observable<ApiResponse<StaffLeave>> {
		return this.http.get<ApiResponse<StaffLeave>>(`${this.apiUrl}/admin/leaves/${id}`);
	}

	getByStaff(staffId: number): Observable<ApiResponse<{ staff: any; leaves: StaffLeave[] }>> {
		return this.http.get<ApiResponse<{ staff: any; leaves: StaffLeave[] }>>(`${this.apiUrl}/admin/leaves/staff/${staffId}`);
	}

	create(data: LeaveRequest): Observable<ApiResponse<StaffLeave>> {
		return this.http.post<ApiResponse<StaffLeave>>(`${this.apiUrl}/admin/leaves`, data);
	}

	bulkCreate(data: BulkLeaveRequest): Observable<ApiResponse<{ created: StaffLeave[]; skipped_dates: string[] }>> {
		return this.http.post<ApiResponse<{ created: StaffLeave[]; skipped_dates: string[] }>>(`${this.apiUrl}/admin/leaves/bulk`, data);
	}

	update(id: number, data: LeaveRequest): Observable<ApiResponse<StaffLeave>> {
		return this.http.put<ApiResponse<StaffLeave>>(`${this.apiUrl}/admin/leaves/${id}`, data);
	}

	delete(id: number): Observable<ApiResponse<null>> {
		return this.http.delete<ApiResponse<null>>(`${this.apiUrl}/admin/leaves/${id}`);
	}
}
